import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt'
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { relativeTime } from '../../shared/relativeTime'
import type { ConversationMeta } from '../../shared/types'

export default function ConversationList(
  { items, activeId, onSelect, onRename, onShare, onDelete, emptyText }:
  {
    items: ConversationMeta[]
    activeId?: string
    onSelect: (id: string) => void
    onRename?: (id: string, title: string) => void
    onShare?: (id: string) => void
    onDelete?: (id: string) => void
    emptyText?: string
  },
) {
  const intl = useIntl()
  const [editingId, setEditingId] = useState<string | undefined>()
  const [draft, setDraft] = useState('')

  const commit = (c: ConversationMeta) => {
    const title = draft.trim()
    setEditingId(undefined)
    if (onRename && title && title !== (c.title ?? '')) {
      onRename(c.id, title)
    }
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
        {emptyText ?? intl.formatMessage({ id: 'caChat.list.empty' })}
      </Typography>
    )
  }

  return (
    <List dense={true} disablePadding={true} sx={{ py: 0.5 }}>
      {items.map((c) => {
        const isShared = c.access === 'viewer' || c.access === 'editor'
        const when = relativeTime(c.createdAt)
        const secondary = isShared
          ? (c.access === 'editor'
            ? intl.formatMessage({ id: 'caChat.list.sharedWith.canChat' })
            : intl.formatMessage({ id: 'caChat.list.sharedWith.viewOnly' }))
          : when
        return (
          <ListItemButton
            key={c.id}
            selected={c.id === activeId}
            onClick={() => onSelect(c.id)}
            sx={{
              borderRadius: 1,
              mx: 0.5,
              pl: 1,
              borderLeft: '2px solid transparent',
              '&.Mui-selected': {
                borderLeftColor: 'primary.main',
              },
              '&:hover .ca-row-actions, &.Mui-selected .ca-row-actions': {
                opacity: 1,
              },
            }}
          >
            {editingId === c.id ? (
              <TextField
                size="small"
                fullWidth={true}
                autoFocus={true}
                value={draft}
                variant="standard"
                onChange={(e) => setDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { commit(c) }
                  if (e.key === 'Escape') { setEditingId(undefined) }
                }}
                onBlur={() => commit(c)}
              />
            ) : (
              <>
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  {isShared
                    ? <PeopleAltOutlinedIcon fontSize="small" />
                    : <ChatBubbleOutlineIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={c.title || c.id}
                  secondary={secondary}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
                {!isShared && (onShare || onRename || onDelete) && (
                  <Box
                    className="ca-row-actions"
                    sx={{ display: 'flex', opacity: 0, transition: 'opacity 0.15s' }}
                  >
                    {onShare && (
                      <Tooltip title={intl.formatMessage({ id: 'caChat.list.tooltip.share' })}>
                        <IconButton
                          aria-label={intl.formatMessage({ id: 'caChat.list.aria.share' })}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onShare(c.id)
                          }}
                        >
                          <PersonAddIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onRename && (
                      <Tooltip title={intl.formatMessage({ id: 'caChat.list.tooltip.rename' })}>
                        <IconButton
                          aria-label={intl.formatMessage({ id: 'caChat.list.aria.rename' })}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDraft(c.title ?? '')
                            setEditingId(c.id)
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title={intl.formatMessage({ id: 'caChat.list.tooltip.delete' })}>
                        <IconButton
                          aria-label={intl.formatMessage({ id: 'caChat.list.aria.delete' })}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDelete(c.id)
                          }}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </>
            )}
          </ListItemButton>
        )
      })}
    </List>
  )
}
